import type { MobileRequestView } from "@vaulted/shared";
import { formatCents, parseDollars } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { errorMessage, get, post } from "../api";
import { time12 } from "../components/AppointmentDialog";
import { Alert, Badge, Button, Checkbox, Field, Loading, PageHeader, SelectField, formatDateTime } from "../components/ui";
import { useSession } from "../session";
import { BackLink } from "../components/BackLink";

const STATUS: Record<MobileRequestView["status"], { label: string; tone: "ok" | "wait" | "off" | "brand" }> = {
  pending: { label: "Waiting for the deposit", tone: "off" },
  requested: { label: "Waiting", tone: "wait" },
  confirmed: { label: "Confirmed", tone: "ok" },
  declined: { label: "Declined", tone: "off" },
  expired: { label: "Cancelled: no answer", tone: "off" },
};

export const whenRequested = (r: MobileRequestView) => `${new Date(`${r.start.slice(0, 10)}T12:00:00Z`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" })}, ${time12(r.start)}–${time12(r.end)}`;

/** How many requests are waiting (for the side menu). */
export function useWaitingRequests() {
  const { can } = useSession();
  return useQuery({
    queryKey: ["mobile-requests", "requested"],
    queryFn: () => get<MobileRequestView[]>("/api/mobile-requests?status=requested"),
    enabled: can("mobile.confirm", "appointments.manage"),
    refetchInterval: 60_000,
  });
}

export function MobileRequestsPage() {
  // Waiting requests are on the Front desk; this lists the last 14 days.
  const status = "recent";
  const list = useQuery({ queryKey: ["mobile-requests", status], queryFn: () => get<MobileRequestView[]>(`/api/mobile-requests?status=${status}`), refetchInterval: 60_000 });
  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <BackLink to="/front-desk" label="Front desk" />
      <PageHeader title="Mobile requests: last 14 days" description="Waiting requests are on the Front desk. Unanswered requests text the nurses, then managers, then the owner." />
      {list.isLoading ? (
        <Loading />
      ) : list.error ? (
        <Alert>{errorMessage(list.error)}</Alert>
      ) : !list.data?.length ? (
        <p className="py-6 text-ink-soft">No mobile requests in the last 14 days.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {list.data
            .slice()
            .sort((a, b) => a.start.localeCompare(b.start))
            .map((r) => (
              <li key={r.bookingId}>
                <Link to={`/mobile-requests/${r.bookingId}`} className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-[var(--radius-panel)] border border-line bg-white px-4 py-3 hover:border-brand">
                  <span className="font-semibold">{whenRequested(r)}</span>
                  <span className="text-sm text-ink-soft">
                    {r.guests.length} guest{r.guests.length > 1 ? "s" : ""} · {r.address.split(",").slice(-2, -1)[0]?.trim() || r.address} · {r.distanceMiles ?? "?"} mi
                  </span>
                  <span className="ml-auto flex items-center gap-2">
                    {r.canAccept && <Badge tone="brand">Open to you</Badge>}
                    <Badge tone={STATUS[r.status].tone}>{STATUS[r.status].label}</Badge>
                  </span>
                </Link>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}

export function MobileRequestPage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const nav = useNavigate();
  const { can } = useSession();
  const isManager = can("mobile.confirm");
  const r = useQuery({ queryKey: ["mobile-request", id], queryFn: () => get<MobileRequestView>(`/api/mobile-requests/${id}`) });
  const [nurse, setNurse] = useState("");
  const [fee, setFee] = useState("");
  const [feeOk, setFeeOk] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState("");
  const done = async (v: MobileRequestView) => {
    qc.setQueryData(["mobile-request", id], v);
    await qc.invalidateQueries({ queryKey: ["mobile-requests"] });
    await qc.invalidateQueries({ queryKey: ["calendar"] });
  };
  const feeCents = fee.trim() ? parseDollars(fee) : null;
  const accept = useMutation({
    mutationFn: () => post<MobileRequestView>(`/api/mobile-requests/${id}/accept`, { staffId: nurse || null, travelFeeCents: feeCents, travelFeeApproved: feeOk }),
    onSuccess: done,
  });
  const decline = useMutation({ mutationFn: () => post<MobileRequestView>(`/api/mobile-requests/${id}/decline`, { reason }), onSuccess: done });

  if (r.isLoading) return <Loading />;
  if (r.error || !r.data) return <Alert>{errorMessage(r.error)}</Alert>;
  const v = r.data;
  const feeChanged = feeCents != null && feeCents !== v.travelFeeCents;

  return (
    <div className="flex max-w-2xl flex-col gap-4">
      <BackLink to="/front-desk" label="Front desk" />
      <header className="flex flex-wrap items-center gap-3">
        <h1 className="page-title">{whenRequested(v)}</h1>
        <Badge tone={STATUS[v.status].tone}>{STATUS[v.status].label}</Badge>
      </header>

      <section className="rounded-[var(--radius-panel)] border border-line bg-white p-4">
        <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
          <div className="sm:col-span-2">
            <dt className="text-muted">Where</dt>
            <dd className="font-semibold">
              {v.address}{" "}
              <a href={v.mapUrl} target="_blank" rel="noreferrer" className="font-normal text-brand underline">
                Map
              </a>
            </dd>
            <dd className="text-ink-soft">
              {v.locationType} · {v.distanceMiles ?? "?"} miles{v.distanceFrom ? ` from ${v.distanceFrom}` : ""} · travel fee {formatCents(v.travelFeeCents)}
            </dd>
            {v.outsideArea && (
              <dd className="mt-1">
                <Badge tone="wait">Outside coverage area</Badge> <span className="text-xs text-muted">The client was told it may be declined.</span>
              </dd>
            )}
          </div>
          <div>
            <dt className="text-muted">Booked by</dt>
            <dd className="font-semibold">
              <Link to={`/clients/${v.booker.id}`} className="underline">
                {v.booker.name}
              </Link>
            </dd>
            <dd className="text-ink-soft">
              {v.booker.mobile} · {v.booker.returning ? "Returning client" : "New client"}
            </dd>
          </div>
          <div>
            <dt className="text-muted">Nurse</dt>
            <dd className="font-semibold">{v.staffName ?? "Not assigned"}</dd>
            {v.status === "requested" && <dd className="text-ink-soft">{v.candidates.length ? `Free: ${v.candidates.map((c) => c.name).join(", ")}` : "No nurse is free then"}</dd>}
          </div>
        </dl>
        <ul className="mt-4 flex flex-col gap-1.5 border-t border-mist pt-3 text-sm">
          {v.guests.map((g) => (
            <li key={g.appointmentId} className="flex justify-between gap-3">
              <span>
                <span className="font-semibold">{g.name}</span> ({g.age}) · {g.serviceName}
                {g.addOns.length > 0 && ` + ${g.addOns.join(", ")}`}
              </span>
              <span className="tabular-nums">{formatCents(g.priceCents)}</span>
            </li>
          ))}
          <li className="flex justify-between gap-3 border-t border-mist pt-1.5 font-semibold">
            <span>Total with travel</span>
            <span className="tabular-nums">{formatCents(v.totalCents)}</span>
          </li>
        </ul>
        {v.notes && <p className="mt-3 text-sm">Note: {v.notes}</p>}
        {v.acknowledgment && <p className="mt-2 text-xs text-muted">Client agreed: “{v.acknowledgment}”</p>}
      </section>

      {v.status === "requested" && v.canAccept && (
        <section className="flex flex-col gap-3 rounded-[var(--radius-panel)] border border-line bg-white p-4">
          {declining ? (
            <form className="flex flex-col gap-3" onSubmit={(e) => (e.preventDefault(), decline.mutate())}>
              <Field label="Why decline?" hint="Kept with the request; the client is told we can't make it." required autoFocus value={reason} onChange={(e) => setReason(e.target.value)} />
              {decline.error && <Alert>{errorMessage(decline.error)}</Alert>}
              <div className="flex gap-2">
                <Button variant="danger" type="submit" busy={decline.isPending}>
                  Decline request
                </Button>
                <Button type="button" variant="quiet" onClick={() => setDeclining(false)}>
                  Back
                </Button>
              </div>
            </form>
          ) : (
            <>
              {isManager && (
                <SelectField label="Nurse" value={nurse} onChange={(e) => setNurse(e.target.value)}>
                  <option value="">{v.staffName ? `Keep ${v.staffName}` : "Pick automatically"}</option>
                  {v.candidates
                    .filter((c) => c.id !== v.staffId)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                </SelectField>
              )}
              <Field label="Travel fee (optional)" inputMode="decimal" placeholder={(v.travelFeeCents / 100).toFixed(2)} value={fee} onChange={(e) => setFee(e.target.value)} hint="The trip's total travel fee. Only enter one the client already approved." />
              {feeChanged && <Checkbox label="The client approved this travel fee before I entered it." checked={feeOk} onChange={setFeeOk} />}
              {accept.error && <Alert>{errorMessage(accept.error)}</Alert>}
              <div className="flex flex-wrap gap-2">
                <Button variant="primary" busy={accept.isPending} disabled={feeChanged && !feeOk} onClick={() => accept.mutate()}>
                  {isManager ? "Confirm request" : "Accept: I'll take it"}
                </Button>
                <Button variant="quiet" onClick={() => setDeclining(true)}>
                  Decline…
                </Button>
              </div>
            </>
          )}
        </section>
      )}
      {v.status !== "requested" && (
        <Alert tone={v.status === "confirmed" ? "ok" : "info"}>
          {v.status === "confirmed" ? `Confirmed by ${v.decidedBy}.` : v.status === "declined" ? `Declined by ${v.decidedBy}: ${v.declineReason}` : "Cancelled automatically: nobody accepted it in time."}
        </Alert>
      )}

      {v.alerts.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer font-semibold text-muted">Texts sent ({v.alerts.length})</summary>
          <ul className="mt-1">
            {v.alerts.map((a, i) => (
              <li key={i}>
                {formatDateTime(a.at)}: {a.name} ({a.level === "nurse" ? "nurse" : a.level === "manager" ? "manager" : "escalation"})
              </li>
            ))}
          </ul>
        </details>
      )}
      <p className="text-xs text-muted">Requested {formatDateTime(v.requestedAt)} · {v.reference}</p>
    </div>
  );
}
